import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Plus, Pencil, Check, AlertTriangle } from 'lucide-react';
import {
  getAppVersions,
  createAppVersion,
  updateAppVersion,
  getAppConfig,
  updateAppConfig,
} from '../services/api';
import { appVersionSchema, AppVersionFormData } from '../utils/validators';
import Card from '../components/ui/Card';
import Badge from '../components/ui/Badge';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Modal from '../components/ui/Modal';
import { TableSkeleton } from '../components/ui/Skeleton';
import { formatDate } from '../utils/formatters';
import type { AppVersion } from '../types';
import toast from 'react-hot-toast';

export default function AppSettingsPage() {
  const qc = useQueryClient();
  const [versionModal, setVersionModal] = useState<AppVersion | 'new' | null>(null);

  const { data: versions, isLoading: versionsLoading } = useQuery({
    queryKey: ['app-versions'],
    queryFn: getAppVersions,
  });

  const { data: configs, isLoading: configsLoading } = useQuery({
    queryKey: ['app-config'],
    queryFn: getAppConfig,
  });

  return (
    <div className="space-y-6">
      {/* App Versions */}
      <Card className="overflow-hidden">
        <div className="p-6 border-b border-border flex items-center justify-between">
          <h3 className="text-base font-semibold text-text-primary">App Versions</h3>
          <Button size="sm" icon={<Plus className="w-4 h-4" />} onClick={() => setVersionModal('new')}>
            Add New Version
          </Button>
        </div>

        {versionsLoading ? (
          <div className="p-4">
            <TableSkeleton rows={3} />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-surface">
                  {['Platform', 'Version', 'Min Supported', 'Force Update', 'Release Notes', 'Actions'].map(
                    (h) => (
                      <th
                        key={h}
                        className="px-5 py-3 text-left text-[11px] font-semibold text-text-hint uppercase tracking-wider"
                      >
                        {h}
                      </th>
                    )
                  )}
                </tr>
              </thead>
              <tbody>
                {(versions || []).map((v) => (
                  <tr key={v.id} className="border-b border-border/50 hover:bg-surface/50 transition-colors">
                    <td className="px-5 py-4 text-sm font-medium text-text-primary capitalize">
                      {v.platform}
                    </td>
                    <td className="px-5 py-4 text-sm font-mono text-text-primary">{v.version}</td>
                    <td className="px-5 py-4 text-sm font-mono text-text-secondary">
                      {v.minSupportedVersion}
                    </td>
                    <td className="px-5 py-4">
                      <Badge variant={v.forceUpdate ? 'success' : 'default'}>
                        {v.forceUpdate ? 'Yes' : 'No'}
                      </Badge>
                    </td>
                    <td className="px-5 py-4 text-sm text-text-secondary max-w-[200px] truncate">
                      {v.releaseNotes || '—'}
                    </td>
                    <td className="px-5 py-4">
                      <button
                        onClick={() => setVersionModal(v)}
                        className="p-1.5 rounded-lg hover:bg-surface text-text-hint hover:text-primary transition-colors"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
                {(!versions || versions.length === 0) && (
                  <tr>
                    <td colSpan={6} className="px-5 py-8 text-center text-sm text-text-hint">
                      No versions added yet
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* App Config */}
      <Card className="p-6">
        <h3 className="text-base font-semibold text-text-primary mb-1">App Configuration</h3>
        <p className="text-sm text-text-hint mb-5">
          These values are fetched by the app at startup
        </p>

        {configsLoading ? (
          <TableSkeleton rows={4} />
        ) : (
          <div className="space-y-3">
            {(configs || []).map((config) => (
              <ConfigRow key={config.key} configKey={config.key} configValue={config.value} />
            ))}
            {(!configs || configs.length === 0) && (
              <p className="text-sm text-text-hint text-center py-4">No config values found</p>
            )}
          </div>
        )}
      </Card>

      {/* Version Modal */}
      {versionModal && (
        <VersionModal
          version={versionModal === 'new' ? undefined : versionModal}
          onClose={() => setVersionModal(null)}
        />
      )}
    </div>
  );
}

function ConfigRow({ configKey, configValue }: { configKey: string; configValue: string }) {
  const qc = useQueryClient();
  const [value, setValue] = useState(configValue);
  const [saved, setSaved] = useState(false);
  const changed = value !== configValue;

  const update = useMutation({
    mutationFn: () => updateAppConfig(configKey, value),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['app-config'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast.success(`${configKey} updated`);
    },
    onError: () => toast.error('Failed to update'),
  });

  return (
    <div className="flex items-center gap-3 bg-surface rounded-btn px-4 py-3">
      <code className="text-[13px] text-text-hint font-mono min-w-[180px]">{configKey}</code>
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="flex-1 bg-white border border-border rounded-btn px-3 py-1.5 text-sm text-text-primary focus:ring-2 focus:ring-primary/20 focus:border-transparent"
      />
      {saved ? (
        <Check className="w-5 h-5 text-green-500" />
      ) : (
        <Button
          size="sm"
          variant={changed ? 'primary' : 'ghost'}
          disabled={!changed}
          loading={update.isPending}
          onClick={() => update.mutate()}
        >
          Save
        </Button>
      )}
    </div>
  );
}

function VersionModal({
  version,
  onClose,
}: {
  version?: AppVersion;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const isEdit = !!version;

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<AppVersionFormData>({
    resolver: zodResolver(appVersionSchema),
    defaultValues: version
      ? {
          platform: version.platform,
          version: version.version,
          minSupportedVersion: version.minSupportedVersion,
          downloadUrl: version.downloadUrl,
          releaseNotes: version.releaseNotes,
          forceUpdate: version.forceUpdate,
        }
      : {
          platform: 'android',
          version: '',
          minSupportedVersion: '',
          downloadUrl: '',
          releaseNotes: '',
          forceUpdate: false,
        },
  });

  const forceUpdate = watch('forceUpdate');

  const create = useMutation({
    mutationFn: (data: AppVersionFormData) => createAppVersion(data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['app-versions'] });
      toast.success('Version added');
      onClose();
    },
    onError: () => toast.error('Failed to add version'),
  });

  const update = useMutation({
    mutationFn: (data: AppVersionFormData) => updateAppVersion(version!.id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['app-versions'] });
      toast.success('Version updated');
      onClose();
    },
    onError: () => toast.error('Failed to update version'),
  });

  const onSubmit = (data: AppVersionFormData) => {
    if (isEdit) {
      update.mutate(data);
    } else {
      create.mutate(data);
    }
  };

  return (
    <Modal open title={isEdit ? 'Edit Version' : 'Add New Version'} onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1.5">Platform</label>
          <select
            {...register('platform')}
            className="w-full bg-surface border-0 rounded-btn px-4 py-2.5 text-sm text-text-primary focus:ring-2 focus:ring-primary/20"
          >
            <option value="android">Android</option>
            <option value="ios">iOS</option>
          </select>
        </div>

        <Input label="Version" placeholder="1.0.0" {...register('version')} error={errors.version?.message} />
        <Input
          label="Min Supported Version"
          placeholder="1.0.0"
          {...register('minSupportedVersion')}
          error={errors.minSupportedVersion?.message}
        />
        <Input
          label="Download URL"
          placeholder="https://..."
          {...register('downloadUrl')}
          error={errors.downloadUrl?.message}
        />

        <div>
          <label className="block text-sm font-medium text-text-primary mb-1.5">
            Release Notes
          </label>
          <textarea
            {...register('releaseNotes')}
            rows={3}
            className="w-full bg-surface border-0 rounded-btn px-4 py-2.5 text-sm text-text-primary placeholder:text-text-hint focus:ring-2 focus:ring-primary/20 resize-none"
          />
        </div>

        <div className="flex items-center justify-between bg-surface rounded-btn px-4 py-3">
          <span className="text-sm text-text-primary">Force Update</span>
          <input type="checkbox" {...register('forceUpdate')} className="rounded text-primary" />
        </div>

        {forceUpdate && (
          <div className="flex items-start gap-2 bg-amber-50 text-amber-700 rounded-btn p-3 text-sm">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            This will force all users on older versions to update before using the app
          </div>
        )}

        <div className="flex gap-3 justify-end pt-2">
          <Button variant="secondary" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={create.isPending || update.isPending}>
            {isEdit ? 'Save Changes' : 'Add Version'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
